import React, { useRef } from 'react';

import type { PopperProps } from '@mui/material';
import { Divider, Paper, Popper } from '@mui/material';
import { useClickAway } from 'react-use';
import { SearchInput } from 'src/components/SearchInput';
import { domElIdCreators } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

export const columnMenuPopperWidthPx = 300;

const useStyles = makeStyles()(theme => ({
  paper: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 440,
    width: columnMenuPopperWidthPx,
  },
  popper: {
    zIndex: theme.zIndex.modal,
  },
  scrollContainer: {
    overflowY: 'auto',
  },
  searchContainer: {
    padding: theme.spacing(1, 2),
  },
}));

interface ColumnMenuPopperSearch {
  placeholder: string;
  setValue: (value: string) => void;
  value: string;
}

interface ColumnMenuPopperProps {
  children: React.ReactNode;
  onClose: () => void;
  popperProps: Omit<PopperProps, 'children'>;
  search?: ColumnMenuPopperSearch;
}

export function ColumnMenuPopper({
  children,
  onClose,
  popperProps: { anchorEl, placement, ...rest },
  search,
}: ColumnMenuPopperProps): JSX.Element {
  const { classes } = useStyles();
  const popperRef = useRef<HTMLDivElement>(null);

  useClickAway(popperRef, onClose);

  return (
    <Popper
      ref={popperRef}
      anchorEl={anchorEl}
      className={classes.popper}
      id={domElIdCreators.questionTypeMenuEl()}
      placement={placement}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
      {...rest}
    >
      <Paper className={classes.paper}>
        <ColumnMenuPopperSearchBar search={search} />
        {/* Stop wheel event propagation to prevent parent column menu from closing when scrolling */}
        <div className={classes.scrollContainer} onWheel={e => e.stopPropagation()}>
          {children}
        </div>
      </Paper>
    </Popper>
  );
}

function ColumnMenuPopperSearchBar({
  search,
}: {
  search: ColumnMenuPopperSearch | undefined;
}): JSX.Element | null {
  const { classes } = useStyles();
  if (!search) return null;

  return (
    <>
      <div className={classes.searchContainer}>
        <SearchInput
          placeholder={search.placeholder}
          search={search.value}
          setSearch={search.setValue}
          style={{ minWidth: 'unset' }}
        />
      </div>
      <Divider />
    </>
  );
}
